import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AlertService } from 'src/app/services/alert/alert.service';
import { Clipboard } from '@angular/cdk/clipboard';
import { ComponentType, FormType, GeneralCondition } from 'src/app/common/enumerations/enumerations';
import { ChecklistTemplate } from 'src/app/components/checklists/checklists';
import { WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { utils } from 'src/app/modules/libs/utils';

@Component({
  selector: 'cl-editor-conditions-help',
  templateUrl: './cl-editor-conditions-help.component.html',
  styleUrls: ['./cl-editor-conditions-help.component.scss']
})
export class ClEditorConditionsHelpComponent {
  @Input() condition?: string;
  @Input() conditions?: string[];
  @Input() formType?: FormType;
  @Input() formTypeName?: string;
  @Input() checklistConditions?: string[];
  @Input() checklistTasks?: WfTaskLocalResource[];
  @Input() checklistTemplate?: ChecklistTemplate;
  @Output() conditionChange = new EventEmitter<string>();

  generalConditions: GeneralConditionInfo[] = [
    { value: GeneralCondition.AllTasksCompleted, description: 'Returns <b class="blue">true</b> if all tasks are marked as completed' },
    { value: GeneralCondition.ChecklistComplete, description: 'Returns <b class="blue">true</b> if the checklist is marked as complete' },
    { value: GeneralCondition.PreviousTasksCompleted, description: 'Returns <b class="blue">true</b> if all previous tasks are completed' },
    { value: GeneralCondition.AnySubsequentTaskSigned, description: 'Returns <b class="blue">true</b> if any subsequent task has been signed' },
    { value: GeneralCondition.SigConditionMet, description: 'Returns <b class="blue">true</b> if the task referenced by the sig condition has been signed by the current user' },
  ];

  operators: Operator[] = [
    {
      op: '&&', definition: 'and',
      example: '<b class="mr-1">Tag1 && Tag2</b>Evaluates if both Tag1 and Tag2 are true'
    },
    {
      op: '||', definition: 'or',
      example: '<b class="mr-1">Tag1 || Tag2</b>Evaluates if either Tag1 or Tag2 is true'
    },
    {
      op: '==', definition: 'equals',
      example: '<b class="mr-1">Tag1 == Tag2</b>Evaluates if Tag1 is equal to Tag2<br/><b class="mr-1">Tag1 == 2</b>Evaluates if Tag1 is equal to a particular number (In this case to 2)'
    },
    {
      op: '!=', definition: 'not equals',
      example: '<b class="mr-1">Tag1 != Tag2</b>Evaluates if Tag1 is not equal to Tag2<br /><b class="mr-1">Tag1 != 2</b>Evaluates if Tag1 is not equal to a particular number (In this case to 2)'
    },
    { op: '!', definition: 'not', example: '<b class="mr-1">!Tag1</b>Evaluates if Tag1 is falsy' },
    { op: '', definition: 'no operator', example: '<b class="mr-1">Tag1</b>Evaluates if Tag1 is true' },
  ]

  constructor(
    private clipboard: Clipboard,
    private alert: AlertService
  ) { }

  /**
   * Returns option values for Multiple Status Change tasks referenced by the current condition.
   */
  get multipleStatusConditionOptions(): MultipleStatusConditionOption[] {
    return (this.checklistTasks ?? [])
      .filter(task => task.componentID == ComponentType.MultipleStatusChange)
      // Show option IDs only when the condition is already referencing that component tag.
      .filter(task => this.conditionContainsTaskCode(task.code))
      .flatMap(task => this.getMultipleStatusOptions(task));
  }

  /**
   * Checks whether the current condition references a task code.
   * @param code Task tag/code to look for in the condition text.
   * @returns True when the code is present in the condition.
   */
  private conditionContainsTaskCode(code?: string): boolean {
    return !!code && (this.condition ?? '').includes(code);
  }

  /**
   * Builds condition helper rows from a Multiple Status Change task configuration.
   * @param task Multiple Status Change task referenced by the condition.
   * @returns Condition values and examples for the configured radio options.
   */
  private getMultipleStatusOptions(task: WfTaskLocalResource): MultipleStatusConditionOption[] {
    const configuration = utils.JSONparse(task.configuration);
    return (configuration?.options ?? [])
      .filter((option: MultipleStatusOption) => option.id != null)
      .map((option: MultipleStatusOption) => {
        const statusDescription = this.checklistTemplate?.documentType?.checklistExecStatuses
          ?.find(status => status.id == option.status)?.description;
        return {
          taskCode: task.code ?? '',
          value: option.id,
          label: option.label ?? '',
          statusDescription,
          example: `${task.code} == ${option.id}`
        };
      });
  }

  copyToClipboard(value: string) {
    if (!this.condition) this.condition = '';
    this.condition = this.condition + value + ' ';
    this.alert.info(`<b>${this.condition}</b> has been copied to the <b>Clipboard</b>`, 'Clipboard');
    this.clipboard.copy(this.condition);
    this.conditionChange.emit(this.condition);
  }
}

interface Operator {
  op: string,
  definition: string,
  example: string
}

interface GeneralConditionInfo {
  value: string,
  description: string
}

interface MultipleStatusOption {
  id?: number;
  label?: string;
  status?: number | null;
}

interface MultipleStatusConditionOption {
  taskCode: string;
  value?: number;
  label: string;
  statusDescription?: string;
  example: string;
}
